"use client";

import { c as _c } from "react/compiler-runtime";
import type { OnboardingReferenceData, OnboardingSportSkillScaleReference } from "@clutch/auth/reference-data";
import { ArrowRight01Icon, Location01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { getDistricts } from "@/app/onboarding/_lib/location-data";
import { Button } from "@/components/ui/button";
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList, useComboboxAnchor } from "@/components/ui/combobox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { getSportConfig } from "@/config/sports";
import { type FillProfileGapsInput, fillProfileGapsAction } from "@/lib/profile-gap/actions";
import { cn } from "@/lib/utils";
export type TournamentSkillRestrictionHint = {
  scaleName: string;
  levelNames: string[];
};
export type TournamentSportContext = {
  slug: string;
  label: string;
  /**
   * Sport slugs already on the user's profile. Used to compose the final
   * sports list when implicitly adding the tournament's sport.
   */
  existingSports: string[];
  /** True when the tournament has skill restrictions. */
  requiresSkillLevel: boolean;
  /** Available scales for this sport (empty if skill levels not enabled). */
  scales: OnboardingSportSkillScaleReference[];
  /**
   * Slug of the scale the tournament expects, when set. Used to default the
   * scale select so the user answers in the right "language". The user can
   * still switch to any scale.
   */
  preferredScaleSlug?: string | null;
  /** True when the tournament's sport is already on the user's profile. */
  alreadyOnProfile: boolean;
  /** True when the user already has a saved scale+level for this sport. */
  hasSavedSkillLevel: boolean;
  /**
   * Inline hint shown above the level picker so the user knows what the
   * tournament accepts before choosing.
   */
  skillRestrictionHint?: TournamentSkillRestrictionHint | null;
};
interface ProfileGapDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  needsLocation: boolean;
  referenceData: OnboardingReferenceData;
  defaultState?: string;
  defaultDistrict?: string;
  /**
   * Tournament context, when the dialog is opened from a tournament register
   * gate. The dialog will auto-add the tournament's sport (no picker) and ask
   * for a skill level when required.
   */
  tournamentSport?: TournamentSportContext;
  onComplete?: () => void;
  title?: string;
  description?: string;
}
type FieldErrors = Partial<Record<"state" | "district" | "skillLevel", string>>;
function pickDefaultScale(scales: OnboardingSportSkillScaleReference[]): OnboardingSportSkillScaleReference | null {
  if (scales.length === 0) return null;
  return scales.find(scale => scale.isDefault) ?? scales[0] ?? null;
}
export function ProfileGapDialog(t0) {
  const $ = _c(93);
  const {
    open,
    onOpenChange,
    needsLocation,
    referenceData,
    defaultState: t1,
    defaultDistrict: t2,
    tournamentSport,
    onComplete,
    title: t3,
    description: t4
  } = t0;
  const defaultState = t1 === undefined ? "" : t1;
  const defaultDistrict = t2 === undefined ? "" : t2;
  const title = t3 === undefined ? "A few quick details" : t3;
  const description = t4 === undefined ? "We need this to show you the right tournaments." : t4;
  const willAddSport = Boolean(tournamentSport && !tournamentSport.alreadyOnProfile);
  const needsSkillLevel = Boolean(tournamentSport && !tournamentSport.hasSavedSkillLevel && tournamentSport.scales.length > 0 && (tournamentSport.requiresSkillLevel || willAddSport));
  let t5;
  if ($[0] !== tournamentSport?.scales) {
    t5 = tournamentSport?.scales ?? [];
    $[0] = tournamentSport?.scales;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const scales = t5;
  let t6;
  if ($[2] !== scales || $[3] !== tournamentSport) {
    t6 = tournamentSport?.preferredScaleSlug ? scales.find(scale => scale.slug === tournamentSport.preferredScaleSlug) : null;
    $[2] = scales;
    $[3] = tournamentSport;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const preferred = t6;
  let t7;
  if ($[5] !== preferred || $[6] !== scales) {
    t7 = preferred ?? pickDefaultScale(scales);
    $[5] = preferred;
    $[6] = scales;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const defaultScale = t7;
  const [state, setState] = useState(defaultState);
  const [district, setDistrict] = useState(defaultDistrict);
  const [scaleSlug, setScaleSlug] = useState(defaultScale?.slug ?? "");
  const [levelSlug, setLevelSlug] = useState("");
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {};
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const [errors, setErrors] = useState(t8);
  const stateAnchor = useComboboxAnchor();
  const districtAnchor = useComboboxAnchor();
  let t9;
  if ($[9] !== referenceData || $[10] !== state) {
    t9 = getDistricts(referenceData, state);
    $[9] = referenceData;
    $[10] = state;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const availableDistricts = t9;
  let t10;
  bb0: {
    if (!tournamentSport) {
      t10 = null;
      break bb0;
    }
    let t11;
    if ($[12] !== defaultScale || $[13] !== scaleSlug || $[14] !== tournamentSport.scales) {
      t11 = tournamentSport.scales.find(scale_0 => scale_0.slug === scaleSlug) ?? defaultScale;
      $[12] = defaultScale;
      $[13] = scaleSlug;
      $[14] = tournamentSport.scales;
      $[15] = t11;
    } else {
      t11 = $[15];
    }
    t10 = t11;
  }
  const selectedScale = t10;
  let t11;
  if ($[16] !== district || $[17] !== levelSlug || $[18] !== needsLocation || $[19] !== needsSkillLevel || $[20] !== scaleSlug || $[21] !== state || $[22] !== tournamentSport || $[23] !== willAddSport) {
    t11 = async () => {
      const payload = {};
      if (needsLocation) {
        payload.location = {
          state: state.trim(),
          district: district.trim()
        };
      }
      if (tournamentSport && (willAddSport || needsSkillLevel)) {
        const nextSports = willAddSport ? Array.from(new Set([...tournamentSport.existingSports, tournamentSport.slug])) : tournamentSport.existingSports;
        payload.sports = {
          sports: nextSports,
          sportProfiles: needsSkillLevel ? [{
            sportSlug: tournamentSport.slug,
            skillScaleSlug: scaleSlug,
            skillLevelSlug: levelSlug
          }] : []
        };
      }
      const result = await fillProfileGapsAction(payload);
      if (!result.ok) {
        throw new Error(result.message);
      }
      return result;
    };
    $[16] = district;
    $[17] = levelSlug;
    $[18] = needsLocation;
    $[19] = needsSkillLevel;
    $[20] = scaleSlug;
    $[21] = state;
    $[22] = tournamentSport;
    $[23] = willAddSport;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== onComplete || $[26] !== onOpenChange) {
    t12 = () => {
      toast.success("Profile updated");
      onOpenChange(false);
      onComplete?.();
    };
    $[25] = onComplete;
    $[26] = onOpenChange;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t11 || $[29] !== t12) {
    t13 = {
      mutationFn: t11,
      onSuccess: t12,
      onError: _temp
    };
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const submitMutation = useMutation(t13);
  let t14;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = function clearError(key) {
      setErrors(prev => {
        if (!prev[key]) {
          return prev;
        }
        const next = {
          ...prev
        };
        delete next[key];
        return next;
      });
    };
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const clearError = t14;
  let t15;
  if ($[32] !== district || $[33] !== levelSlug || $[34] !== needsLocation || $[35] !== needsSkillLevel || $[36] !== scaleSlug || $[37] !== selectedScale?.levelNoun || $[38] !== state || $[39] !== submitMutation) {
    t15 = function handleSubmit() {
      const next_0 = {};
      if (needsLocation) {
        if (!state.trim()) {
          next_0.state = "Select your state";
        }
        if (!district.trim()) {
          next_0.district = "Select your district";
        }
      }
      if (needsSkillLevel) {
        if (!scaleSlug || !levelSlug) {
          next_0.skillLevel = `Select your ${selectedScale?.levelNoun.toLowerCase() ?? "level"}`;
        }
      }
      if (Object.keys(next_0).length > 0) {
        setErrors(next_0);
        toast.error("Please complete the highlighted fields");
        return;
      }
      submitMutation.mutate();
    };
    $[32] = district;
    $[33] = levelSlug;
    $[34] = needsLocation;
    $[35] = needsSkillLevel;
    $[36] = scaleSlug;
    $[37] = selectedScale?.levelNoun;
    $[38] = state;
    $[39] = submitMutation;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  const handleSubmit = t15;
  let t16;
  if ($[41] !== tournamentSport) {
    t16 = tournamentSport ? getSportConfig(tournamentSport.slug) : null;
    $[41] = tournamentSport;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  const sportConfig = t16;
  const SportIcon = sportConfig?.icon;
  const showScalePicker = needsSkillLevel && (tournamentSport?.scales.length ?? 0) > 1;
  let t17;
  if ($[43] !== selectedScale?.levels) {
    t17 = selectedScale?.levels ?? [];
    $[43] = selectedScale?.levels;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  const levelOptions = t17;
  let t18;
  if ($[45] !== title) {
    t18 = <DialogTitle className="font-semibold text-xl tracking-tight sm:text-2xl">{title}</DialogTitle>;
    $[45] = title;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== description) {
    t19 = <DialogDescription className="text-base leading-6">{description}</DialogDescription>;
    $[47] = description;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t18 || $[50] !== t19) {
    t20 = <DialogHeader>{t18}{t19}</DialogHeader>;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== availableDistricts || $[53] !== district || $[54] !== districtAnchor || $[55] !== errors || $[56] !== needsLocation || $[57] !== referenceData || $[58] !== state || $[59] !== stateAnchor) {
    t21 = needsLocation ? <div className="space-y-4"><div className="space-y-2.5"><Label htmlFor="profile-gap-state" className="gap-1">State <span className="text-destructive">*</span></Label><Combobox value={state || ""} onValueChange={val => {
          const next_1 = val || "";
          setState(next_1);
          clearError("state");
          if (next_1 !== state) {
            setDistrict("");
          }
        }} items={referenceData.states} itemToStringValue={_temp2} autoHighlight={true}><div ref={stateAnchor} className="relative w-full"><div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-4 text-foreground"><HugeiconsIcon icon={Location01Icon} size={20} /></div><ComboboxInput id="profile-gap-state" placeholder="Search or select state" className={cn("h-12 w-full rounded-xl border border-border/50 bg-transparent pr-4 pl-11 focus-visible:ring-1 focus-visible:ring-primary/50 dark:border-0", errors.state && "ring-1 ring-destructive")} aria-invalid={Boolean(errors.state)} showClear={true} /></div><ComboboxContent anchor={stateAnchor} className="rounded-2xl border border-border/50 bg-popover px-1 py-1.5 text-popover-foreground dark:border-0"><ComboboxEmpty className="py-6 text-center text-muted-foreground text-sm">No state found.</ComboboxEmpty><ComboboxList className="max-h-64">{_temp3}</ComboboxList></ComboboxContent></Combobox>{errors.state ? <p role="alert" className="font-medium text-destructive text-sm">{errors.state}</p> : null}</div><div className="space-y-2.5"><Label htmlFor="profile-gap-district" className="gap-1">District <span className="text-destructive">*</span></Label><Combobox value={district || ""} onValueChange={val_0 => {
          setDistrict(val_0 || "");
          clearError("district");
        }} items={availableDistricts} itemToStringValue={_temp4} disabled={!state} autoHighlight={true}><div ref={districtAnchor} className="relative w-full"><div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-4 text-foreground"><HugeiconsIcon icon={Location01Icon} size={20} /></div><ComboboxInput id="profile-gap-district" placeholder={state ? "Search or select district" : "Select state first"} className={cn("h-12 w-full rounded-xl border border-border/50 bg-transparent pr-4 pl-11 focus-visible:ring-1 focus-visible:ring-primary/50 disabled:opacity-40 dark:border-0", errors.district && "ring-1 ring-destructive")} aria-invalid={Boolean(errors.district)} disabled={!state} showClear={true} /></div><ComboboxContent anchor={districtAnchor} className="rounded-2xl border border-border/50 bg-popover px-1 py-1.5 text-popover-foreground dark:border-0"><ComboboxEmpty className="py-6 text-center text-muted-foreground text-sm">No district found.</ComboboxEmpty><ComboboxList className="max-h-64">{_temp5}</ComboboxList></ComboboxContent></Combobox>{errors.district ? <p role="alert" className="font-medium text-destructive text-sm">{errors.district}</p> : null}</div></div> : null;
    $[52] = availableDistricts;
    $[53] = district;
    $[54] = districtAnchor;
    $[55] = errors;
    $[56] = needsLocation;
    $[57] = referenceData;
    $[58] = state;
    $[59] = stateAnchor;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== errors || $[62] !== levelOptions || $[63] !== levelSlug || $[64] !== needsLocation || $[65] !== needsSkillLevel || $[66] !== scaleSlug || $[67] !== selectedScale || $[68] !== showScalePicker || $[69] !== tournamentSport) {
    t22 = needsSkillLevel && tournamentSport && selectedScale ? <div className="space-y-3">{showScalePicker ? <div className="space-y-2.5">{needsLocation ? <Label htmlFor="profile-gap-scale" className="gap-1">Scale <span className="text-destructive">*</span></Label> : <Label htmlFor="profile-gap-scale" className="text-muted-foreground text-xs">Scale</Label>}<Select value={scaleSlug || undefined} onValueChange={value => {
          setScaleSlug(value);
          setLevelSlug("");
          clearError("skillLevel");
        }}><SelectTrigger id="profile-gap-scale" className="h-12 w-full rounded-xl border border-border/50 bg-transparent px-4 dark:border-0"><SelectValue placeholder="Select a scale" /></SelectTrigger><SelectContent className="rounded-xl">{tournamentSport.scales.map(_temp6)}</SelectContent></Select></div> : null}<div className="space-y-2.5">{needsLocation ? <Label htmlFor="profile-gap-level" className="gap-1">{selectedScale.levelNoun}{" "}<span className="text-destructive">*</span></Label> : null}<Select value={levelSlug || undefined} onValueChange={value_0 => {
          setLevelSlug(value_0);
          clearError("skillLevel");
        }}><SelectTrigger id="profile-gap-level" className={cn("h-12 w-full rounded-xl border border-border/50 bg-transparent px-4 dark:border-0", errors.skillLevel && "ring-1 ring-destructive")} aria-invalid={Boolean(errors.skillLevel)}><SelectValue placeholder={showScalePicker ? `Select your ${selectedScale.levelNoun.toLowerCase()}` : `Select your ${selectedScale.levelNoun.toLowerCase()} (${selectedScale.name})`} /></SelectTrigger><SelectContent className="rounded-xl">{levelOptions.map(_temp7)}</SelectContent></Select>{errors.skillLevel ? <p role="alert" className="font-medium text-destructive text-sm">{errors.skillLevel}</p> : null}</div></div> : null;
    $[61] = errors;
    $[62] = levelOptions;
    $[63] = levelSlug;
    $[64] = needsLocation;
    $[65] = needsSkillLevel;
    $[66] = scaleSlug;
    $[67] = selectedScale;
    $[68] = showScalePicker;
    $[69] = tournamentSport;
    $[70] = t22;
  } else {
    t22 = $[70];
  }
  let t23;
  if ($[71] !== SportIcon || $[72] !== tournamentSport || $[73] !== willAddSport) {
    t23 = willAddSport && tournamentSport && SportIcon ? <div className="flex items-center gap-3 rounded-xl border border-border/50 bg-muted/30 px-4 py-3 dark:border-0"><div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-background"><SportIcon size={18} className="opacity-80" /></div><p className="text-sm leading-5">We'll add{" "}<span className="font-semibold">{tournamentSport.label}</span>{" "}to your sports.</p></div> : null;
    $[71] = SportIcon;
    $[72] = tournamentSport;
    $[73] = willAddSport;
    $[74] = t23;
  } else {
    t23 = $[74];
  }
  let t24;
  if ($[75] !== t21 || $[76] !== t22 || $[77] !== t23) {
    t24 = <div className="space-y-5 py-2">{t21}{t22}{t23}</div>;
    $[75] = t21;
    $[76] = t22;
    $[77] = t23;
    $[78] = t24;
  } else {
    t24 = $[78];
  }
  let t25;
  if ($[79] !== submitMutation.isPending) {
    t25 = submitMutation.isPending ? <Spinner className="size-4" /> : <>Save and continue<HugeiconsIcon icon={ArrowRight01Icon} className="size-4" /></>;
    $[79] = submitMutation.isPending;
    $[80] = t25;
  } else {
    t25 = $[80];
  }
  let t26;
  if ($[81] !== handleSubmit || $[82] !== submitMutation.isPending || $[83] !== t25) {
    t26 = <DialogFooter><Button type="button" size="lg" onClick={handleSubmit} disabled={submitMutation.isPending} className="h-12 w-full px-8 sm:w-auto">{t25}</Button></DialogFooter>;
    $[81] = handleSubmit;
    $[82] = submitMutation.isPending;
    $[83] = t25;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  let t27;
  if ($[85] !== t20 || $[86] !== t24 || $[87] !== t26) {
    t27 = <DialogContent className="sm:max-w-lg">{t20}{t24}{t26}</DialogContent>;
    $[85] = t20;
    $[86] = t24;
    $[87] = t26;
    $[88] = t27;
  } else {
    t27 = $[88];
  }
  let t28;
  if ($[89] !== onOpenChange || $[90] !== open || $[91] !== t27) {
    t28 = <Dialog open={open} onOpenChange={onOpenChange}>{t27}</Dialog>;
    $[89] = onOpenChange;
    $[90] = open;
    $[91] = t27;
    $[92] = t28;
  } else {
    t28 = $[92];
  }
  return t28;
}
function _temp7(level) {
  return <SelectItem key={level.slug} value={level.slug}>{level.name}</SelectItem>;
}
function _temp6(scale_1) {
  return <SelectItem key={scale_1.slug} value={scale_1.slug}>{scale_1.name}</SelectItem>;
}
function _temp5(item_2) {
  return <ComboboxItem key={item_2} value={item_2} className="rounded-xl px-4 py-2">{item_2}</ComboboxItem>;
}
function _temp4(item_1) {
  return item_1;
}
function _temp3(item_0) {
  return <ComboboxItem key={item_0} value={item_0} className="rounded-xl px-4 py-2">{item_0}</ComboboxItem>;
}
function _temp2(item) {
  return item;
}
function _temp(error) {
  toast.error(error.message);
}